import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const PerformanceReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState({ status: '', review_cycle: '' });
  const [formData, setFormData] = useState({
    employee_id: '',
    review_cycle: `Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${new Date().getFullYear()}`,
    review_type: 'quarterly',
    overall_score: '',
    strengths: '',
    areas_for_improvement: '',
    goals_achieved: '',
    goals_next_period: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchReviews();
    fetchEmployees();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.review_cycle) params.append('review_cycle', filter.review_cycle);
      
      const response = await api.get(`/hr/performance/reviews?${params.toString()}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      const reviewData = {
        ...formData,
        overall_score: formData.overall_score ? parseFloat(formData.overall_score) : null
      };
      await api.post('/hr/performance/reviews', reviewData);
      toast.success('Avaliação criada com sucesso');
      setShowModal(false);
      setFormData({
        employee_id: '',
        review_cycle: `Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${new Date().getFullYear()}`,
        review_type: 'quarterly',
        overall_score: '',
        strengths: '',
        areas_for_improvement: '',
        goals_achieved: '',
        goals_next_period: ''
      });
      fetchReviews();
    } catch (error) {
      console.error('Error creating review:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar avaliação');
    }
  };

  const handleUpdateStatus = async (reviewId, status) => {
    try {
      await api.put(`/hr/reviews/${reviewId}`, { status });
      toast.success('Estado atualizado');
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Erro ao atualizar avaliação');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-blue-100 text-blue-700',
      acknowledged: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      draft: 'Rascunho',
      submitted: 'Submetida',
      acknowledged: 'Vista',
      completed: 'Concluída'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const labels = {
      annual: 'Anual',
      quarterly: 'Trimestral',
      probation: 'Período Experimental',
      ad_hoc: 'Pontual'
    };
    return labels[type] || type;
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-emerald-600';
    if (score >= 3) return 'text-blue-600';
    if (score >= 2) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 4.5) return 'Excecional';
    if (score >= 3.5) return 'Excelente';
    if (score >= 2.5) return 'Bom';
    if (score >= 1.5) return 'A Melhorar';
    return 'Insuficiente';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="performance-reviews-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Avaliações de Desempenho</h1>
          <p className="text-muted-foreground">Gestão de avaliações e feedback</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Avaliação
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900/30">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{reviews.filter(r => r.status === 'draft').length}</p>
              <p className="text-xs text-muted-foreground">Rascunhos</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{reviews.filter(r => r.status === 'submitted').length}</p>
              <p className="text-xs text-muted-foreground">Submetidas</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{reviews.filter(r => r.status === 'completed').length}</p>
              <p className="text-xs text-muted-foreground">Concluídas</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold">{reviews.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="draft">Rascunho</option>
              <option value="submitted">Submetida</option>
              <option value="acknowledged">Vista</option>
              <option value="completed">Concluída</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ciclo</label>
            <input
              type="text"
              value={filter.review_cycle}
              onChange={(e) => setFilter({ ...filter, review_cycle: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Ex: Q1-2025, Annual-2025"
            />
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="rounded-xl border bg-card p-8 text-center">
            <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-muted-foreground">Sem avaliações</p>
          </div>
        ) : (
          reviews.map((review) => {
            const employee = employees.find(e => e.employee_id === review.employee_id);
            return (
              <div key={review.review_id} className="rounded-xl border bg-card p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium bg-muted px-2 py-1 rounded">{review.review_cycle}</span>
                      <span className="text-sm text-muted-foreground">{getTypeBadge(review.review_type)}</span>
                      {getStatusBadge(review.status)}
                    </div>
                    <h3 className="font-semibold text-lg">{employee?.name || review.employee_id}</h3>
                    <p className="text-sm text-muted-foreground">{employee?.position} - {employee?.department}</p>
                    
                    {review.overall_score && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Score:</span>
                        <span className={`text-2xl font-bold ${getScoreColor(review.overall_score)}`}>
                          {review.overall_score.toFixed(1)}
                        </span>
                        <span className={`text-sm ${getScoreColor(review.overall_score)}`}>
                          ({getScoreLabel(review.overall_score)})
                        </span>
                      </div>
                    )}

                    {review.strengths && (
                      <div className="mt-3">
                        <p className="text-sm font-medium">Pontos Fortes:</p>
                        <p className="text-sm text-muted-foreground">{review.strengths}</p>
                      </div>
                    )}

                    {review.areas_for_improvement && (
                      <div className="mt-2">
                        <p className="text-sm font-medium">Áreas a Melhorar:</p>
                        <p className="text-sm text-muted-foreground">{review.areas_for_improvement}</p>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-3">
                      Data: {review.review_date} | Avaliador: {review.reviewer_name || review.reviewer_id}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {review.status === 'draft' && (
                      <button
                        onClick={() => handleUpdateStatus(review.review_id, 'submitted')}
                        className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-medium transition-colors"
                      >
                        Submeter
                      </button>
                    )}
                    {review.status === 'submitted' && (
                      <button
                        onClick={() => handleUpdateStatus(review.review_id, 'acknowledged')}
                        className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 text-sm font-medium transition-colors"
                      >
                        Marcar como Vista
                      </button>
                    )}
                    {review.status === 'acknowledged' && (
                      <button
                        onClick={() => handleUpdateStatus(review.review_id, 'completed')}
                        className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-sm font-medium transition-colors"
                      >
                        Concluir
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Review Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Nova Avaliação de Desempenho</h2>
            </div>
            <form onSubmit={handleCreateReview} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Colaborador</label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar colaborador</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>{emp.name} - {emp.position}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ciclo de Avaliação</label>
                  <input
                    type="text"
                    value={formData.review_cycle}
                    onChange={(e) => setFormData({ ...formData, review_cycle: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Ex: Q1-2025"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select
                    value={formData.review_type}
                    onChange={(e) => setFormData({ ...formData, review_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="quarterly">Trimestral</option>
                    <option value="annual">Anual</option>
                    <option value="probation">Período Experimental</option>
                    <option value="ad_hoc">Pontual</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Score Global (1-5)</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={formData.overall_score}
                    onChange={(e) => setFormData({ ...formData, overall_score: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Ex: 3.5"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Pontos Fortes</label>
                  <textarea
                    value={formData.strengths}
                    onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={3}
                    placeholder="Descreva os pontos fortes do colaborador..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Áreas a Melhorar</label>
                  <textarea
                    value={formData.areas_for_improvement}
                    onChange={(e) => setFormData({ ...formData, areas_for_improvement: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={3}
                    placeholder="Identifique áreas de melhoria..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Objetivos Alcançados</label>
                  <textarea
                    value={formData.goals_achieved}
                    onChange={(e) => setFormData({ ...formData, goals_achieved: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={2}
                    placeholder="Objetivos do período anterior..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Objetivos Próximo Período</label>
                  <textarea
                    value={formData.goals_next_period}
                    onChange={(e) => setFormData({ ...formData, goals_next_period: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={2}
                    placeholder="Objetivos para o próximo período..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Criar Avaliação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceReviewsPage;
